const DATA_MANAGER_IGNORED_HISTORY_FIELDS = new Set([
  "deliveries",
  "orders",
  "source_kind",
  "source_label",
  "updated_at",
]);

const CLIENT_HISTORY_FIELDS = new Set([
  "client_code",
  "client_name",
  "correspondence_name",
  "courriel",
  "telephone",
  "indication",
  "address",
  "is_meunerie",
  "is_coop",
  "bill_to",
  "meunerie_loading_extra_minutes",
  "loading_opening_time",
  "loading_closing_time",
]);

const EXECUTION_DERIVED_HISTORY_FIELDS = new Set([
  "billing_approval_status",
  "can_approve_for_billing",
  "can_clear_delivered",
  "can_reopen",
  "can_revoke_for_billing",
  "dispatch_status",
  "is_cancellable",
  "is_editable",
  "lock_reason",
]);

function normalizeAuditFieldName(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeClientHistoryValue(value) {
  return typeof value === "string" ? value.trim() || null : value ?? null;
}

export function resolveDataManagerClientAuditAction(action, fallbackLabel) {
  const normalizedAction = normalizeAuditFieldName(action).toLowerCase();
  const presentations = {
    create: { actionLabel: "Création", actionClassName: "border-accent bg-accent text-white" },
    update: { actionLabel: "Modification", actionClassName: "border-blue-700 bg-blue-700 text-white" },
    delete: { actionLabel: "Suppression", actionClassName: "border-red-700 bg-red-700 text-white" },
  };
  return presentations[normalizedAction] || {
    actionLabel: fallbackLabel,
    actionClassName: "border-slate-300 bg-white text-slate-700",
  };
}

export function resolveDataManagerHistorySortState(sortState, activeTab) {
  return sortState.key === "action" && activeTab !== "correspondences"
    ? { direction: "desc", key: "date" }
    : sortState;
}

export function resolveDataManagerClientAuditSummaryChange(entry) {
  const entityKind = normalizeAuditFieldName(entry?.entity_kind).toLowerCase();
  const action = normalizeAuditFieldName(entry?.action).toLowerCase();
  if (entityKind !== "client_correspondence" || (action !== "create" && action !== "delete")) {
    return null;
  }
  const clientLabel = normalizeAuditFieldName(entry?.entity_label)
    || normalizeAuditFieldName(entry?.entity_id) || "Client";
  return {
    field: "client_record",
    old_value: action === "delete" ? clientLabel : null,
    new_value: action === "create" ? clientLabel : null,
  };
}

function findAuditFieldChange(fieldChanges, fieldName) {
  return fieldChanges.find(
    (fieldChange) => normalizeAuditFieldName(fieldChange?.field) === fieldName,
  );
}

function formatExecutionTonnageValue(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return `${numericValue} t`;
  }
  return String(value).trim() || null;
}

function resolveDeliveredTonnageSummary(deliveriesChange) {
  if (!Array.isArray(deliveriesChange?.new_value)) {
    return null;
  }

  const deliveredTonnages = deliveriesChange.new_value
    .map((delivery) => formatExecutionTonnageValue(delivery?.actual_delivered_quantity_tons))
    .filter(Boolean);

  if (deliveredTonnages.length === 0) {
    return null;
  }

  return `${deliveredTonnages.join(" / ")} livré`;
}

function resolveExecutionAuditFieldChange(entry) {
  const fieldChanges = Array.isArray(entry?.field_changes) ? entry.field_changes : [];
  const summary = normalizeAuditFieldName(entry?.summary);
  const isDriverOnlyUpdate = summary.toLowerCase().includes("chauffeur livreur");

  const loadedChange = findAuditFieldChange(fieldChanges, "actual_loaded_quantity_tons");
  const dispatchChange = findAuditFieldChange(fieldChanges, "dispatch_status");
  const deliveriesChange = findAuditFieldChange(fieldChanges, "deliveries");
  const markedDelivered = dispatchChange?.new_value === "completed"
    && dispatchChange?.old_value !== "completed";

  const detailParts = [];
  const loadedTonnage = formatExecutionTonnageValue(loadedChange?.new_value);
  if (loadedTonnage) {
    detailParts.push(`${loadedTonnage} chargé`);
  }

  const deliveredTonnageSummary = resolveDeliveredTonnageSummary(deliveriesChange);
  if (deliveredTonnageSummary) {
    detailParts.push(deliveredTonnageSummary);
  }

  if (markedDelivered) {
    detailParts.push("marquée livrée");
  }

  const newValue = detailParts.length > 0 ? detailParts.join(" · ") : (summary || "Enregistré");

  if (isDriverOnlyUpdate) {
    return [{ field: "execution_driver_updated", old_value: null, new_value: newValue }];
  }

  return [{ field: "execution_tonnages_recorded", old_value: null, new_value: newValue }];
}

function resolveDeletedOrderFieldChange(entry) {
  const entityId = normalizeAuditFieldName(entry?.entity_id);
  if (!entityId) {
    return null;
  }

  return [{ field: "deleted_order_id", old_value: entityId, new_value: null }];
}

export function resolveDataManagerAuditFieldChanges(entry) {
  const normalizedEntityKind = normalizeAuditFieldName(entry?.entity_kind).toLowerCase();
  const normalizedAction = normalizeAuditFieldName(entry?.action).toLowerCase();
  const fieldChanges = Array.isArray(entry?.field_changes) ? entry.field_changes : [];
  if (normalizedEntityKind === "client_correspondence") {
    return fieldChanges.filter((change) => (
      CLIENT_HISTORY_FIELDS.has(normalizeAuditFieldName(change?.field))
      && [change.old_value, change.new_value].every((value) => value == null || ["string", "number", "boolean"].includes(typeof value))
      && normalizeClientHistoryValue(change.old_value) !== normalizeClientHistoryValue(change.new_value)
    ));
  }
  const isOrderDeletion = normalizedEntityKind === "order" && normalizedAction === "delete";
  if (isOrderDeletion) {
    return resolveDeletedOrderFieldChange(entry) || [];
  }

  const isOrderCreation = normalizedEntityKind === "order" && normalizedAction === "create";
  if (isOrderCreation) {
    const createdOrderIdsChange = fieldChanges.find(
      (fieldChange) => normalizeAuditFieldName(fieldChange?.field) === "created_order_ids",
    );
    if (createdOrderIdsChange) {
      return [createdOrderIdsChange];
    }

    const entityId = normalizeAuditFieldName(entry?.entity_id);
    if (entityId && !/^bulk-create:/i.test(entityId)) {
      return [{ field: "created_order_ids", old_value: null, new_value: [entityId] }];
    }
  }

  const isOrderExecution = normalizedEntityKind === "order" && normalizedAction === "execution";
  if (isOrderExecution) {
    return resolveExecutionAuditFieldChange(entry);
  }

  return fieldChanges.filter(
    (fieldChange) => !DATA_MANAGER_IGNORED_HISTORY_FIELDS.has(normalizeAuditFieldName(fieldChange?.field)),
  );
}

export function resolveDataManagerAuditFieldNames(fieldNames, entry = null) {
  const normalizedEntityKind = normalizeAuditFieldName(entry?.entity_kind).toLowerCase();
  const normalizedAction = normalizeAuditFieldName(entry?.action).toLowerCase();
  if (normalizedEntityKind === "client_correspondence") {
    const visibleFields = Array.isArray(entry?.field_changes) && entry.field_changes.length > 0
      ? new Set(resolveDataManagerAuditFieldChanges(entry).map((change) => normalizeAuditFieldName(change.field)))
      : CLIENT_HISTORY_FIELDS;
    return Array.isArray(fieldNames) ? fieldNames.filter((name) => visibleFields.has(normalizeAuditFieldName(name))) : [];
  }
  if (normalizedEntityKind === "order" && normalizedAction === "execution") {
    return ["execution_tonnages_recorded"];
  }

  return Array.isArray(fieldNames)
    ? fieldNames.filter((fieldName) => !DATA_MANAGER_IGNORED_HISTORY_FIELDS.has(normalizeAuditFieldName(fieldName)))
    : [];
}

export function formatDataManagerAuditSourceLabel(sourceKind) {
  const normalizedSourceKind = normalizeAuditFieldName(sourceKind).toLowerCase();
  if (normalizedSourceKind === "manual") {
    return "Saisie manuelle";
  }
  if (normalizedSourceKind === "import") {
    return "Import Excel";
  }
  if (normalizedSourceKind === "sollio_import") {
    return "Import Sollio";
  }
  return normalizedSourceKind.replace(/_/g, " ") || "—";
}

function resolveDataManagerCreatedOrderIds(entry) {
  const fieldChanges = Array.isArray(entry?.field_changes) ? entry.field_changes : [];
  const createdOrderIdsChange = fieldChanges.find(
    (fieldChange) => normalizeAuditFieldName(fieldChange?.field) === "created_order_ids",
  );
  if (Array.isArray(createdOrderIdsChange?.new_value) && createdOrderIdsChange.new_value.length > 0) {
    return createdOrderIdsChange.new_value
      .map((value) => normalizeAuditFieldName(value))
      .filter(Boolean);
  }

  const entityId = normalizeAuditFieldName(entry?.entity_id);
  if (entityId && !/^bulk-create:\d+$/i.test(entityId)) {
    return [entityId];
  }

  return [];
}

export function formatDataManagerAuditOrderNumberLabel(entry) {
  const entityKind = normalizeAuditFieldName(entry?.entity_kind).toLowerCase();
  if (entityKind !== "order" && entityKind !== "order_import") {
    return "—";
  }

  const createdOrderIds = resolveDataManagerCreatedOrderIds(entry);
  if (createdOrderIds.length > 0) {
    return createdOrderIds.join(", ");
  }

  const entityId = normalizeAuditFieldName(entry?.entity_id);
  if (!entityId || /^bulk-create:\d+$/i.test(entityId) || entityId.toLowerCase() === "orders") {
    return "—";
  }

  return entityId;
}

export function resolveDataManagerCreatedOrderCount(entry) {
  if (
    normalizeAuditFieldName(entry?.entity_kind).toLowerCase() !== "order"
    || normalizeAuditFieldName(entry?.action).toLowerCase() !== "create"
  ) {
    return null;
  }

  const createdOrderIdsChange = Array.isArray(entry?.field_changes)
    ? entry.field_changes.find(
      (fieldChange) => normalizeAuditFieldName(fieldChange?.field) === "created_order_ids",
    )
    : null;
  if (Array.isArray(createdOrderIdsChange?.new_value) && createdOrderIdsChange.new_value.length > 0) {
    return createdOrderIdsChange.new_value.length;
  }

  const entityId = normalizeAuditFieldName(entry?.entity_id);
  const bulkCountMatch = entityId.match(/^bulk-create:(\d+)$/i);
  if (bulkCountMatch) {
    return Number(bulkCountMatch[1]);
  }

  return entityId ? 1 : null;
}
